import React, { useEffect, useState } from 'react';
import api, { unwrap } from '../utils/api';

// Ad links: one tracked link per place you advertise. Give the advertiser
// https://itsasimcha.net/go/<name>; every visit through it is counted here,
// along with orders placed within 30 days of the visit.

const SITE = (process.env.REACT_APP_SITE_URL || 'https://itsasimcha.net').replace(/\/+$/, '');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Stat({ label, value, sub }) {
  return (
    <div className="adlink-stat">
      <div className="adlink-stat-value">{value}</div>
      <div className="adlink-stat-label">{label}</div>
      {sub && <div className="adlink-stat-sub">{sub}</div>}
    </div>
  );
}

// Daily clicks, last 30 days: one series, thin bars, hover for the value.
function DailyBars({ days }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...days.map(d => d.clicks));
  const W = 900; const H = 110; const gap = 2;
  const bw = W / days.length - gap;
  return (
    <div className="adlink-chart">
      <div className="adlink-chart-title">Clicks per day, last 30 days</div>
      <div style={{ position: 'relative' }}>
        <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" role="img" aria-label="Clicks per day for the last 30 days">
          <line x1="0" x2={W} y1={H + 0.5} y2={H + 0.5} stroke="#e3e6ea" />
          {days.map((d, i) => {
            const h = d.clicks ? Math.max(3, (d.clicks / max) * (H - 6)) : 0;
            const x = i * (bw + gap);
            return (
              <g key={d.date} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                {/* hit target: the full column, bigger than the bar */}
                <rect x={x} y="0" width={bw + gap} height={H} fill="transparent" />
                {h > 0 && (
                  <path
                    d={`M${x},${H} v${-(h - 4)} q0,-4 4,-4 h${bw - 8} q4,0 4,4 v${h - 4} z`}
                    fill={hover === i ? '#2a5fae' : '#3b7ddd'}
                  />
                )}
              </g>
            );
          })}
          {[0, 10, 20, 29].map(i => (
            <text key={i} x={i === 0 ? 0 : i === 29 ? W : i * (bw + gap) + bw / 2} y={H + 14} textAnchor={i === 0 ? 'start' : i === 29 ? 'end' : 'middle'} fontSize="11" fill="#8a94a0">{dayLabel(days[i].date)}</text>
          ))}
        </svg>
        {hover != null && (
          <div className="adlink-tip" style={{ left: `${((hover + 0.5) / days.length) * 100}%` }}>
            <strong>{days[hover].clicks}</strong> click{days[hover].clicks === 1 ? '' : 's'} · {dayLabel(days[hover].date)}
          </div>
        )}
      </div>
      <details className="adlink-table">
        <summary>Show as table</summary>
        <table className="table">
          <thead><tr><th>Day</th><th>Clicks</th></tr></thead>
          <tbody>
            {days.slice().reverse().map(d => <tr key={d.date}><td>{dayLabel(d.date)}</td><td>{d.clicks}</td></tr>)}
          </tbody>
        </table>
      </details>
    </div>
  );
}

export default function AdLinks() {
  const [links, setLinks] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');
  const [form, setForm] = useState({ name: '', destination: '/' });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get('/links', { params: { tzOffset: -new Date().getTimezoneOffset() } });
      setLinks(unwrap(r) || []);
      setErr('');
    } catch (e) {
      setErr('Could not load ad links.');
    }
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/links', { name: form.name, destination: form.destination });
      setForm({ name: '', destination: '/' });
      await load();
    } catch (e2) {
      const m = e2?.response?.data?.body;
      setErr(typeof m === 'string' ? m : 'Could not create the link.');
    } finally {
      setSaving(false);
    }
  }

  async function toggle(link) {
    await api.post(`/links/${link._id}/active`, { active: !link.active });
    load();
  }

  function copy(url) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(''), 2000);
    });
  }

  return (
    <>
      <h1>Ad links</h1>
      <p style={{ color: '#666', marginTop: -8 }}>
        Give each place you advertise its own link. Every visit through it is counted here, plus orders
        placed within 30 days of the visit. Link previews (WhatsApp, iMessage) and bots are not counted.
      </p>
      {err && <p style={{ color: '#b42318' }}>{err}</p>}

      {links === null && <p>Loading…</p>}
      {links && links.length === 0 && <div className="card">No ad links yet. Create one below.</div>}
      {links && links.map(link => {
        // The page to open travels in the link, so the site never waits on the server.
        const url = `${SITE}/go/${link.slug}${link.destination && link.destination !== '/' ? `?to=${encodeURIComponent(link.destination)}` : ''}`;
        return (
          <div className="card" key={link._id} style={{ opacity: link.active ? 1 : 0.7 }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0 }}>{link.name}{!link.active && <span className="adlink-paused">Paused</span>}</h2>
                <div className="row" style={{ marginTop: 6 }}>
                  <code className="adlink-url">{url}</code>
                  <button className="btn small" onClick={() => copy(url)}>{copied === url ? 'Copied' : 'Copy link'}</button>
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Opens {link.destination === '/' ? 'the home page' : link.destination}
                  {link.lastClick ? ` · last click ${new Date(link.lastClick).toLocaleString()}` : ' · no clicks yet'}
                </div>
              </div>
              <button className="btn small secondary" onClick={() => toggle(link)}>{link.active ? 'Pause link' : 'Resume link'}</button>
            </div>

            <div className="adlink-stats">
              <Stat label="Total clicks" value={link.clicks} />
              <Stat label="Unique visitors" value={link.uniqueVisitors} />
              <Stat label="Today" value={link.today} />
              <Stat label="Last 7 days" value={link.last7} />
              <Stat label="Last 30 days" value={link.last30} />
              <Stat label="Orders" value={link.orders} sub={link.orders ? `${money(link.revenue)} revenue` : 'within 30 days of a click'} />
            </div>
            <DailyBars days={link.days} />
          </div>
        );
      })}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>New ad link</h2>
        <form onSubmit={create} className="row">
          <input
            placeholder="Name, e.g. yidstatus"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ flex: '1 1 220px' }}
          />
          <input
            placeholder="Opens page (default: home page /)"
            value={form.destination}
            onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
            style={{ flex: '1 1 220px' }}
          />
          <button className="btn" disabled={saving || !form.name.trim()}>{saving ? 'Creating…' : 'Create link'}</button>
        </form>
        <p style={{ fontSize: 12, color: '#888', margin: '8px 0 0' }}>
          The link will be {SITE}/go/<em>name</em> (letters, numbers and dashes). The page can be any path on the site,
          for example /categories/its-a-boy.
        </p>
      </div>
    </>
  );
}
